import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FileText, Plus, RefreshCw, CheckCircle2, XCircle, Clock, Building2, ShieldAlert, LineChart } from 'lucide-react'
import { listBatches, createBatch, type AuditBatch } from '../../lib/services/chartAudit'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import { useIsMultiFacility } from '../../hooks/useTier'

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: typeof CheckCircle2 }> = {
  completed: { bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle2 },
  processing: { bg: 'bg-blue-50', text: 'text-blue-700', icon: RefreshCw },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock },
  failed: { bg: 'bg-red-50', text: 'text-red-700', icon: XCircle },
}

function ScoreDistributionBar({ dist }: { dist: Record<string, number> | null }) {
  if (!dist) return <span className="text-xs text-slate-400">—</span>
  const total = (dist['1'] ?? 0) + (dist['2'] ?? 0) + (dist['3'] ?? 0)
  if (total === 0) return <span className="text-xs text-slate-400">—</span>
  return (
    <div className="flex h-3 w-24 overflow-hidden rounded-full">
      {dist['1'] > 0 && <div className="bg-green-400" style={{ width: `${(dist['1'] / total) * 100}%` }} />}
      {dist['2'] > 0 && <div className="bg-amber-400" style={{ width: `${(dist['2'] / total) * 100}%` }} />}
      {dist['3'] > 0 && <div className="bg-red-400" style={{ width: `${(dist['3'] / total) * 100}%` }} />}
    </div>
  )
}

export function ChartAuditDashboardPage() {
  const navigate = useNavigate()
  const isMultiFacility = useIsMultiFacility()
  const [batches, setBatches] = useState<AuditBatch[]>([])
  const [facilities, setFacilities] = useState<FacilityOption[]>([])
  const [selectedFacility, setSelectedFacility] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        const [batchList, facilityList] = await Promise.all([
          listBatches(selectedFacility || undefined),
          getFacilities(),
        ])
        if (!cancelled) {
          setBatches(batchList)
          setFacilities(facilityList)
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [selectedFacility])

  const handleCreateBatch = async () => {
    const facilityId = selectedFacility || facilities[0]?.facility_id
    if (!facilityId) return
    setCreating(true)
    try {
      const batch = await createBatch(facilityId, 10)
      navigate(`/chart-audit/${batch.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create batch')
    } finally {
      setCreating(false)
    }
  }

  const completedBatches = batches.filter((b) => b.batch_status === 'completed')
  const totalCharts = completedBatches.reduce((sum, b) => sum + b.processed_count, 0)
  const lastAudit = completedBatches[0]?.completed_at
    ? new Date(completedBatches[0].completed_at).toLocaleDateString()
    : 'Never'

  return (
    <div className="space-y-6">
      <div className="v11-workspace-header flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">Operational module</p>
          <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Chart Audit</h1>
          <p className="mt-1 text-sm text-slate-500">
            CMS §416.47(b) medical records compliance — QAPI integration
          </p>
        </div>
        <button
          onClick={handleCreateBatch}
          disabled={creating || facilities.length === 0}
          className="btn-primary flex items-center gap-2 px-4 py-2.5 text-sm disabled:opacity-50"
        >
          <Plus size={16} />
          {creating ? 'Creating...' : 'New Audit Batch'}
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <Link
          to="/chart-audit/qapi"
        className="v11-dashboard-card inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700"
        >
          <ShieldAlert size={16} className="text-slate-400" />
          Open QAPI Findings
        </Link>
        <Link
          to="/chart-audit/trends"
          className="v11-dashboard-card inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700"
        >
          <LineChart size={16} className="text-slate-400" />
          Open Trends View
        </Link>
        {isMultiFacility ? (
          <Link
            to="/chart-audit/portfolio"
            className="v11-dashboard-card inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700"
          >
            <Building2 size={16} className="text-slate-400" />
            Open Portfolio View
          </Link>
        ) : null}
      </div>

      {/* Summary cards */}
      <div className="v11-kpi-grid">
        <div className="v11-kpi-card">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Batches</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{batches.length}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-teal">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Charts Audited</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{totalCharts}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-gold">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Last Audit</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{lastAudit}</p>
        </div>
      </div>

      {/* Facility filter */}
      {facilities.length > 1 && (
        <select
          value={selectedFacility}
          onChange={(e) => setSelectedFacility(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
        >
          <option value="">All Facilities</option>
          {facilities.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>
          ))}
        </select>
      )}

      {/* Batch list */}
      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">Loading audit batches...</div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : batches.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center">
          <FileText size={40} className="mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-500">No audit batches yet</p>
          <p className="mt-1 text-xs text-slate-400">
            Create a new batch to start auditing charts against CMS §416.47(b)
          </p>
        </div>
      ) : (
        <div className="v11-section">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Batch</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Charts</th>
                <th className="px-4 py-3">Scores</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {batches.map((batch) => {
                const style = STATUS_STYLES[batch.batch_status] || STATUS_STYLES.pending
                const Icon = style.icon
                return (
                  <tr key={batch.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/chart-audit/${batch.id}`} className="font-semibold text-[var(--brand-primary)] hover:underline">
                        {batch.id.slice(0, 8)}...
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${style.bg} ${style.text}`}>
                        <Icon size={12} />
                        {batch.batch_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {batch.processed_count}/{batch.file_count}
                    </td>
                    <td className="px-4 py-3">
                      <ScoreDistributionBar dist={batch.score_distribution} />
                    </td>
                    <td className="px-4 py-3 text-slate-500">
                      {new Date(batch.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
